import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { getRestrictionColor } from '../utils/restrictionLayerHelpers'
import { translateRdppfZoneName } from '../../../../utils'

export interface RestrictionsLegendProps {
  restrictionTypes: Array<{ id: string; name: string }>
  className?: string
  /** Optional translation function for zone names */
  t?: TFunction
}

/**
 * Restrictions Legend Component
 *
 * Displays a categorical legend showing different restriction types and their colors
 */
export const RestrictionsLegend: React.FC<RestrictionsLegendProps> = ({
  restrictionTypes,
  className,
  t,
}) => {
  const { t: tUi } = useTranslation('ui')

  if (!restrictionTypes || restrictionTypes.length === 0) {
    return null
  }

  return (
    <LegendContainer className={className}>
      <LegendTitle>{tUi('analysisMap.restrictionTypes')}</LegendTitle>
      <CategoricalLegend>
        {restrictionTypes.map((type) => (
          <CategoryItem key={type.id}>
            <CategoryColor $color={getRestrictionColor(type.id, type.name)} />
            <CategoryLabel>
              {t ? translateRdppfZoneName(type.name, t) : type.name}
            </CategoryLabel>
          </CategoryItem>
        ))}
      </CategoricalLegend>
    </LegendContainer>
  )
}

// Styled Components

const LegendContainer = styled.div`
  padding: 8px 0;
  width: 100%;
`

const LegendTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
`

const CategoricalLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const CategoryColor = styled.div<{ $color: string }>`
  width: 14px;
  height: 14px;
  background-color: ${({ $color }) => $color};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 2px;
  flex-shrink: 0;
`

const CategoryLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.3;
`
